import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { RedirectModal } from '@/components/ui/redirect-modal'
import { SITE_CONFIG } from '@/config/site'

interface RedirectContextValue {
  /**
   * Open the redirect modal with a target URL
   * @param path - The path to redirect to (will be appended to TARGET_URL with utm_source)
   */
  openRedirect: (path?: string) => void
}

const RedirectContext = createContext<RedirectContextValue | null>(null)

export function RedirectProvider({ children }: { children: ReactNode }) {
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    targetUrl: string
  }>({
    isOpen: false,
    targetUrl: '',
  })

  const openRedirect = useCallback((path: string = '') => {
    const targetUrl = `${SITE_CONFIG.TARGET_URL}${path}?utm_source=${SITE_CONFIG.UTM_SOURCE}`
    setModalState({
      isOpen: true,
      targetUrl,
    })
  }, [])

  const closeRedirect = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
  }, [])

  return (
    <RedirectContext.Provider value={{ openRedirect }}>
      {children}
      <RedirectModal
        isOpen={modalState.isOpen}
        onClose={closeRedirect}
        targetUrl={modalState.targetUrl}
      />
    </RedirectContext.Provider>
  )
}

export function useRedirect() {
  const context = useContext(RedirectContext)
  if (!context) {
    throw new Error('useRedirect must be used within a RedirectProvider')
  }
  return context
}
